import {useCallback, useState} from "react";
import {isInTrialOfferWindow} from "account/trial";
import {nextUpgradePlan, planOf} from "home/gating";
import {AuthenticatedSessionState} from "auth/AuthenticatedSessionWaiter";
import {useAuthenticatedSession, useUser} from "../UserProvider";
import {dismissOfferFor, hasDismissedOfferFor} from "./offerDismissal";
import {
	firstMonthOfferFrom, type FirstMonthOffer, visibleOfferSpotsLeft,
} from "./firstMonthOffer";
import {useFirstMonthOffer} from "./useFirstMonthOffer";
import type {PaidPlanId} from "pricing/planCatalogue";

/** What the trial-expiry callout needs to render itself. */
export interface TrialExpiryOffer {
	/** The offer worth putting on screen, or null to render nothing. */
	offer: FirstMonthOffer | null;
	/** The tier being pitched — the one the trial has been demonstrating. */
	plan: PaidPlanId | null;
	/** The campaign's name key, or null when it is unnamed. */
	occasion: string | null;
	/** The campaign's last open day, or null when it never closes. */
	endsOn: string | null;
	/** The api's real available remainder, or null when it supplies none. */
	spotsLeft: number | null;
	/** Close the offer for this account on this browser. Durable across reloads. */
	dismiss: () => void;
}

/**
 * The 50%-off offer as a referee's trial runs out (kitecorp/transferify-ui#1449) — the third moment of
 * kitecorp/transferify#117.
 *
 * <b>This moment does NOT use the post-signup moment's eligibility rule, and that is the whole
 * subtlety.</b> `GetAccount` answers a trial as `subscriptionActive: true, subscriptionType: BASIC`
 * so that feature gating needs no client change — which means `planOf` reads a trial reader as PAID.
 * The post-signup rule refuses paid readers, so reusing it here would refuse every reader this surface
 * exists for. `trialExpiresAt` being set is what separates a trial from a subscription
 * (`account/trial`), and it is the only eligibility question this surface owns.
 *
 * <b>The api still decides whether there is anything to offer</b>, and it will: the referee trial is an
 * entitlement override with no plan row behind it, so the never-had-a-paid-plan floor and the
 * live-plan check (kitecorp/transferify-api-service#847) both pass a trial reader. Verified against
 * `Account.trialPlan` — "NEVER a billing object: no plan row, no Stripe subscription".
 *
 * <b>Which tier it pitches.</b> `nextUpgradePlan` of the trial's own tier would sell the reader UP from
 * a plan they do not actually hold — the trial ends and they return to Free. The pitch is the tier the
 * trial has been demonstrating, which is what `planOf` reports while the window is live.
 */
export function useTrialExpiryOffer(now: Date): TrialExpiryOffer {
	const [user] = useUser();
	const [session] = useAuthenticatedSession();
	const accountId = user.id;
	const plan = planOf(user);
	// The tier the trial itself is demonstrating, not the one above it. Falls back to the next tier up
	// for a reader whose plan somehow reads free, so the pitch is never null while an offer stands.
	const target: PaidPlanId | null = plan === "free" ? nextUpgradePlan(plan) : plan;
	const [dismissed, setDismissed] = useState(() => hasDismissedOfferFor(accountId));
	const inWindow = isInTrialOfferWindow(user.trialExpiresAt, now);
	// Gated on the authenticated session for the same reason the post-signup moment is: the offer
	// endpoint is authenticated, and an ask made before the auth listener settles answers 401 and is
	// swallowed, leaving an eligible reader with no offer for the whole page load.
	const quoted = useFirstMonthOffer(target,
		inWindow && !dismissed && session === AuthenticatedSessionState.Authenticated);
	const dismiss = useCallback(() => {
		dismissOfferFor(accountId);
		setDismissed(true);
	}, [accountId]);
	const offer = inWindow && !dismissed
		? firstMonthOfferFrom(quoted?.percentOff ?? null)
		: null;
	return {
		offer,
		plan: target,
		occasion: offer === null ? null : quoted?.occasion ?? null,
		endsOn: offer === null ? null : quoted?.endsOn ?? null,
		spotsLeft: offer === null ? null : visibleOfferSpotsLeft(quoted?.spotsLeft),
		dismiss,
	};
}
